import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Session cache for /products/{handle}.js responses, keyed by handle.
   * Stores the in-flight promise so concurrent calls dedupe to one request.
   * @type {Map<string, Promise<any>>}
   */
  #productDataCache = new Map();

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // Add click outside handler for desktop header search
    if (!dialog && this.#isDesktop) {
      // Use capture phase to catch clicks before they bubble
      document.addEventListener('click', this.#handleClickOutside, { signal, capture: true });
    }

    onDocumentLoaded(() => {
      // Only reset when this instance is inside the search modal (not the header).
      // Otherwise every instance would clear its input on window "load", wiping text the user is typing in the header.
      if (this.dialog) {
        this.resetSearch(false); // Pass false to avoid focusing the input
      }
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Checks if we're on desktop
   * @returns {boolean}
   */
  get #isDesktop() {
    return window.innerWidth >= 750;
  }

  /**
   * Handles clicks outside the predictive search component to close the dropdown
   * @param {MouseEvent} event - The mouse event
   */
  #handleClickOutside = (event) => {
    if (!this.#isDesktop) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    // Don't close if clicking on the search input itself (it should stay open when typing)
    if (target === this.refs.searchInput || this.refs.searchInput?.contains(target)) {
      return;
    }

    // Re-fetch contentWrapper to ensure we have the correct element
    const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
    const dropdown = this.refs.predictiveSearchResults?.querySelector('#predictive-search-results');

    // Check if click is outside the component and content wrapper
    const isClickInsideComponent = this.contains(target);
    const isClickInsideDropdown = contentWrapper?.contains(target);

    if (!isClickInsideComponent && !isClickInsideDropdown) {
      // Check if dropdown is actually open (either by aria-expanded or by display style)
      const isOpen =
        dropdown?.getAttribute('aria-expanded') === 'true' ||
        (contentWrapper instanceof HTMLElement &&
          contentWrapper.style.getPropertyValue('display') !== 'none' &&
          window.getComputedStyle(contentWrapper).display !== 'none');

      if (isOpen) {
        this.#closeDropdown();
      }
    }
  };

  /**
   * Gets the dropdown element
   * @returns {HTMLElement | null}
   */
  get #dropdown() {
    return this.refs.predictiveSearchResults?.querySelector('#predictive-search-results') || null;
  }

  /**
   * Gets the content wrapper element
   * @returns {HTMLElement | null}
   */
  get #contentWrapper() {
    return this.querySelector('.predictive-search-form__content-wrapper');
  }

  /**
   * Sets aria-expanded attribute for dropdown and input
   * @param {boolean} expanded - Whether the dropdown is expanded
   */
  #setAriaExpanded(expanded) {
    const value = expanded ? 'true' : 'false';
    if (this.#dropdown) {
      this.#dropdown.setAttribute('aria-expanded', value);
    }
    if (this.refs.searchInput) {
      this.refs.searchInput.setAttribute('aria-expanded', value);
    }
  }

  /**
   * Closes the dropdown without clearing the input value
   */
  #closeDropdown = () => {
    this.#setAriaExpanded(false);
    // Re-fetch contentWrapper to ensure we have the correct element
    const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
    if (contentWrapper instanceof HTMLElement) {
      contentWrapper.style.setProperty('display', 'none', 'important');
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
        '.predictive-search-results__wrapper-products, ' +
        '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Single-token queries (letters/digits/dots/dashes, 3+ chars, no spaces) — typical part code.
   * @param {string} query
   * @returns {boolean}
   */
  #isSkuLikeQuery(query) {
    const t = (query || '').trim();
    if (t.length < 3) return false;
    if (/\s/.test(t)) return false;
    return /^[\w.-]+$/i.test(t);
  }

  /**
   * Exact match on handle, full title, or any variant SKU/barcode.
   * @param {any} product
   * @param {string} handle
   * @param {string} queryLower
   * @returns {boolean}
   */
  #hasExactIdentifierMatch(product, handle, queryLower) {
    const q = (queryLower || '').trim();
    if (!q || !product) return false;
    if ((handle || '').toLowerCase() === q) return true;
    if (String(product.title || '').trim().toLowerCase() === q) return true;
    const variants = product.variants;
    if (!Array.isArray(variants)) return false;
    for (const v of variants) {
      if (String(v?.sku ?? '').trim().toLowerCase() === q) return true;
      if (String(v?.barcode ?? '').trim().toLowerCase() === q) return true;
    }
    return false;
  }

  /**
   * Fetches /products/{handle}.js with per-handle in-memory dedupe + caching.
   * Handles don't change within a session, so results are safe to reuse.
   * @param {string} handle
   * @returns {Promise<any|null>}
   */
  #fetchProductData(handle) {
    const existing = this.#productDataCache.get(handle);
    if (existing) return existing;
    const promise = fetch(`/products/${handle}.js`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => (data?.product ? data.product : data))
      .catch(() => null);
    this.#productDataCache.set(handle, promise);
    return promise;
  }

  /**
   * Shopify predictive search matches part codes like "g100.008" as a prefix/token,
   * so "g100.007" and "g100.009" come back alongside the exact hit. For SKU-like queries,
   * post-filter the response to keep only products whose handle/title/SKU/barcode matches exactly.
   * Mutates parsedDoc in place. Falls back to original results if no exact match is found.
   * @param {Document} parsedDoc
   * @param {string} query
   * @param {AbortController} abortController
   */
  async #applyStrictSkuFilter(parsedDoc, query, abortController) {
    if (!this.#isSkuLikeQuery(query)) return;

    const allCards = Array.from(parsedDoc.querySelectorAll('.predictive-search-results__card'));
    const productCards = allCards.filter((card) => card.querySelector('a[href*="/products/"]'));
    if (productCards.length === 0) return;

    const slideData = productCards
      .map((card) => {
        const anchor = card.querySelector('a[href*="/products/"]');
        const href = anchor?.getAttribute('href') || '';
        const match = href.match(/\/products\/([^/?#]+)/);
        return match && match[1] ? { card, handle: match[1] } : null;
      })
      .filter(/** @returns {d is {card: Element, handle: string}} */ (d) => d !== null);

    if (slideData.length === 0) return;

    const results = await Promise.all(
      slideData.map(async ({ card, handle }) => ({
        card,
        handle,
        product: await this.#fetchProductData(handle),
      }))
    );

    if (abortController.signal.aborted) return;

    const queryLower = query.toLowerCase().trim();
    const matches = results.filter(({ product, handle }) =>
      this.#hasExactIdentifierMatch(product, handle, queryLower)
    );

    if (matches.length === 0) return;

    const keep = new Set(matches.map((m) => m.card));
    for (const { card } of results) {
      if (!keep.has(card)) card.remove();
    }
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * Always uses Predictive Search API for consistent full markup (queries, products, collections, pages, articles).
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Default suggest fields omit SKU/barcode; without this, pasted SKUs (e.g. LINCIONE120PTO) return no results.
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,variants.sku,variants.barcode'
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then(async (resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        const parsedDoc = new DOMParser().parseFromString(resultsMarkup, 'text/html');
        await this.#applyStrictSkuFilter(parsedDoc, searchTerm, abortController);

        if (abortController.signal.aborted) return;

        const finalMarkup = parsedDoc.body.innerHTML;

        morph(predictiveSearchResults, finalMarkup);

        this.#setAriaExpanded(true);

        // Use requestAnimationFrame to ensure DOM is updated before setting display
        requestAnimationFrame(() => {
          if (abortController.signal.aborted) return;

          // Re-fetch contentWrapper after morph to ensure we have the correct element
          const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
          if (contentWrapper instanceof HTMLElement) {
            // Remove display: none (including !important) and set to block to show the dropdown
            // Use block for desktop (as per CSS), flex for mobile
            const displayValue = this.#isDesktop ? 'block' : 'flex';
            contentWrapper.style.setProperty('display', displayValue, 'important');
          }
        });

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */(id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);

    // Set aria-expanded to false when resetting search
    this.#setAriaExpanded(false);

    // Close the dropdown - re-fetch contentWrapper after morph
    requestAnimationFrame(() => {
      const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
      if (contentWrapper instanceof HTMLElement) {
        contentWrapper.style.setProperty('display', 'none', 'important');
      }
    });

    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}



// PREDICTIVE SEARCH + REGULAR SEARCH FROM 3 CHARS IN SEARCH INPUT, ALSO SEE LINES 30-32 IN predictive-search-resource-carousel SNIPPET AND LINES 80-86 IN predictive-search SECTION
// import { Component } from '@theme/component';
// import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
// import { sectionRenderer } from '@theme/section-renderer';
// import { morph } from '@theme/morph';
// import { RecentlyViewed } from '@theme/recently-viewed-products';
// import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

// /**
//  * A custom element that allows the user to search for resources available on the store.
//  *
//  * @typedef {object} Refs
//  * @property {HTMLInputElement} searchInput - The search input element.
//  * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
//  * @property {HTMLElement} resetButton - The reset button element.
//  * @property {HTMLElement[]} [resultsItems] - The search results items elements.
//  * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
//  * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
//  * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
//  * @extends {Component<Refs>}
//  */
// class PredictiveSearchComponent extends Component {
//   requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

//   #controller = new AbortController();

//   /**
//    * @type {AbortController | null}
//    */
//   #activeFetch = null;

//   /**
//    * Get the dialog component.
//    * @returns {DialogComponent | null} The dialog component.
//    */
//   get dialog() {
//     return this.closest('dialog-component');
//   }

//   connectedCallback() {
//     super.connectedCallback();

//     const { dialog } = this;
//     const { signal } = this.#controller;

//     if (this.refs.searchInput.value.length > 0) {
//       this.#showResetButton();
//     }

//     if (dialog) {
//       document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
//       dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

//       this.addEventListener('click', this.#handleModalClick, { signal });
//     }

//     // Add click outside handler for desktop header search
//     if (!dialog && this.#isDesktop) {
//       // Use capture phase to catch clicks before they bubble
//       document.addEventListener('click', this.#handleClickOutside, { signal, capture: true });
//     }

//     onDocumentLoaded(() => {
//       this.resetSearch(false); // Pass false to avoid focusing the input
//     });
//   }

//   /**
//    * Handles clicks within the predictive search modal to maintain focus on the input
//    * @param {MouseEvent} event - The mouse event
//    */
//   #handleModalClick = (event) => {
//     const target = /** @type {HTMLElement} */ (event.target);
//     const isInteractiveElement =
//       target instanceof HTMLButtonElement ||
//       target instanceof HTMLAnchorElement ||
//       target instanceof HTMLInputElement ||
//       target.closest('button') ||
//       target.closest('a') ||
//       target.closest('input');

//     if (!isInteractiveElement && this.refs.searchInput) {
//       this.refs.searchInput.focus();
//     }
//   };

//   /**
//    * Checks if we're on desktop
//    * @returns {boolean}
//    */
//   get #isDesktop() {
//     return window.innerWidth >= 750;
//   }

//   /**
//    * Handles clicks outside the predictive search component to close the dropdown
//    * @param {MouseEvent} event - The mouse event
//    */
//   #handleClickOutside = (event) => {
//     if (!this.#isDesktop) return;

//     const target = event.target;
//     if (!(target instanceof Element)) return;

//     // Don't close if clicking on the search input itself (it should stay open when typing)
//     if (target === this.refs.searchInput || this.refs.searchInput?.contains(target)) {
//       return;
//     }

//     // Re-fetch contentWrapper to ensure we have the correct element
//     const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
//     const dropdown = this.refs.predictiveSearchResults?.querySelector('#predictive-search-results');

//     // Check if click is outside the component and content wrapper
//     const isClickInsideComponent = this.contains(target);
//     const isClickInsideDropdown = contentWrapper?.contains(target);

//     if (!isClickInsideComponent && !isClickInsideDropdown) {
//       // Check if dropdown is actually open (either by aria-expanded or by display style)
//       const isOpen =
//         dropdown?.getAttribute('aria-expanded') === 'true' ||
//         (contentWrapper instanceof HTMLElement &&
//           contentWrapper.style.getPropertyValue('display') !== 'none' &&
//           window.getComputedStyle(contentWrapper).display !== 'none');

//       if (isOpen) {
//         this.#closeDropdown();
//       }
//     }
//   };

//   /**
//    * Gets the dropdown element
//    * @returns {HTMLElement | null}
//    */
//   get #dropdown() {
//     return this.refs.predictiveSearchResults?.querySelector('#predictive-search-results') || null;
//   }

//   /**
//    * Gets the content wrapper element
//    * @returns {HTMLElement | null}
//    */
//   get #contentWrapper() {
//     return this.querySelector('.predictive-search-form__content-wrapper');
//   }

//   /**
//    * Sets aria-expanded attribute for dropdown and input
//    * @param {boolean} expanded - Whether the dropdown is expanded
//    */
//   #setAriaExpanded(expanded) {
//     const value = expanded ? 'true' : 'false';
//     if (this.#dropdown) {
//       this.#dropdown.setAttribute('aria-expanded', value);
//     }
//     if (this.refs.searchInput) {
//       this.refs.searchInput.setAttribute('aria-expanded', value);
//     }
//   }

//   /**
//    * Closes the dropdown without clearing the input value
//    */
//   #closeDropdown = () => {
//     this.#setAriaExpanded(false);
//     // Re-fetch contentWrapper to ensure we have the correct element
//     const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
//     if (contentWrapper instanceof HTMLElement) {
//       contentWrapper.style.setProperty('display', 'none', 'important');
//     }
//   };

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.#controller.abort();
//   }

//   /**
//    * Handles the CMD+K key combination.
//    * @param {KeyboardEvent} event - The keyboard event.
//    */
//   #handleKeyboardShortcut = (event) => {
//     if (event.metaKey && event.key === 'k') {
//       this.dialog?.toggleDialog();
//     }
//   };

//   /**
//    * Handles the dialog close event.
//    */
//   #handleDialogClose = () => {
//     this.#resetSearch();
//   };

//   get #allResultsItems() {
//     const containers = Array.from(
//       this.querySelectorAll(
//         '.predictive-search-results__wrapper-queries, ' +
//         '.predictive-search-results__wrapper-products, ' +
//         '.predictive-search-results__list'
//       )
//     );

//     const allItems = containers
//       .flatMap((container) => {
//         if (container.classList.contains('predictive-search-results__wrapper-products')) {
//           return Array.from(container.querySelectorAll('.predictive-search-results__card'));
//         }
//         return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
//       })
//       .filter((item) => item instanceof HTMLElement);

//     return /** @type {HTMLElement[]} */ (allItems);
//   }

//   /**
//    * Track whether the last interaction was keyboard-based
//    * @type {boolean}
//    */
//   #isKeyboardNavigation = false;

//   get #currentIndex() {
//     return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
//   }

//   set #currentIndex(index) {
//     if (!this.#allResultsItems?.length) return;

//     this.#allResultsItems.forEach((item) => {
//       item.classList.remove('keyboard-focus');
//     });

//     for (const [itemIndex, item] of this.#allResultsItems.entries()) {
//       if (itemIndex === index) {
//         item.setAttribute('aria-selected', 'true');

//         if (this.#isKeyboardNavigation) {
//           item.classList.add('keyboard-focus');
//         }
//         item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
//       } else {
//         item.removeAttribute('aria-selected');
//       }
//     }
//     this.refs.searchInput.focus();
//   }

//   get #currentItem() {
//     return this.#allResultsItems?.[this.#currentIndex];
//   }

//   /**
//    * Navigate through the predictive search results using arrow keys or close them with the Escape key.
//    * @param {KeyboardEvent} event - The keyboard event.
//    */
//   onSearchKeyDown = (event) => {
//     if (event.key === 'Escape') {
//       this.#resetSearch();
//       return;
//     }

//     if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
//       return;
//     }

//     const currentIndex = this.#currentIndex;
//     const totalItems = this.#allResultsItems.length;

//     switch (event.key) {
//       case 'ArrowDown':
//         this.#isKeyboardNavigation = true;
//         event.preventDefault();
//         this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
//         break;

//       case 'Tab':
//         if (event.shiftKey) {
//           this.#isKeyboardNavigation = true;
//           event.preventDefault();
//           this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
//         } else {
//           this.#isKeyboardNavigation = true;
//           event.preventDefault();
//           this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
//         }
//         break;

//       case 'ArrowUp':
//         this.#isKeyboardNavigation = true;
//         event.preventDefault();
//         this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
//         break;

//       case 'Enter': {
//         const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
//         if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
//           event.preventDefault();
//           window.location.href = singleResultContainer.dataset.singleResultUrl;
//           return;
//         }

//         if (this.#currentIndex >= 0) {
//           event.preventDefault();
//           this.#currentItem?.querySelector('a')?.click();
//         } else {
//           const searchUrl = new URL(Theme.routes.search_url, location.origin);
//           searchUrl.searchParams.set('q', this.refs.searchInput.value);
//           window.location.href = searchUrl.toString();
//         }
//         break;
//       }
//     }
//   };

//   /**
//    * Clears the recently viewed products.
//    * @param {Event} event - The event.
//    */
//   clearRecentlyViewedProducts(event) {
//     event.stopPropagation();

//     RecentlyViewed.clearProducts();

//     const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

//     const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

//     if (allRecentlyViewedElements.length === 0) {
//       return;
//     }

//     if (recentlyViewedWrapper) {
//       recentlyViewedWrapper.classList.add('removing');

//       onAnimationEnd(recentlyViewedWrapper, () => {
//         recentlyViewedWrapper.remove();
//       });
//     }
//   }

//   /**
//    * Reset the search state.
//    * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
//    */
//   resetSearch = debounce((keepFocus = true) => {
//     if (keepFocus) {
//       this.refs.searchInput.focus();
//     }
//     this.#resetSearch();
//   }, 100);

//   /**
//    * Debounce the search handler to fetch and display search results based on the input value.
//    * Reset the current selection index and close results if the search term is empty.
//    */
//   search = debounce((event) => {
//     // If the input is not a text input (like using the Escape key), don't search
//     if (!event.inputType) return;

//     const searchTerm = this.refs.searchInput.value.trim();
//     this.#currentIndex = -1;

//     if (!searchTerm.length) {
//       this.#resetSearch();
//       return;
//     }

//     this.#showResetButton();
//     this.#getSearchResults(searchTerm);
//   }, 200);

//   /**
//    * Resets scroll positions for search results containers
//    */
//   #resetScrollPositions() {
//     requestAnimationFrame(() => {
//       const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
//       if (resultsInner instanceof HTMLElement) {
//         resultsInner.scrollTop = 0;
//       }

//       const formContent = this.querySelector('.predictive-search-form__content');
//       if (formContent instanceof HTMLElement) {
//         formContent.scrollTop = 0;
//       }
//     });
//   }

//   /**
//    * Checks if search term might be SKU or barcode (numeric or alphanumeric without spaces)
//    * @param {string} searchTerm - The search term
//    * @returns {boolean}
//    */
//   #mightBeSkuOrBarcode(searchTerm) {
//     // Check if it's mostly alphanumeric without spaces (typical SKU/barcode pattern)
//     // Allow some special characters like dashes, underscores
//     const skuBarcodePattern = /^[a-zA-Z0-9\-_]+$/;
//     return skuBarcodePattern.test(searchTerm) && searchTerm.length >= 3 && !searchTerm.includes(' ');
//   }

//   /**
//    * Fetch search results using the section renderer and update the results container.
//    * @param {string} searchTerm - The term to search for
//    */
//   async #getSearchResults(searchTerm) {
//     if (!this.dataset.sectionId) return;

//     const { predictiveSearchResults } = this.refs;
//     const abortController = this.#createAbortController();

//     // Use regular search for SKU/barcode queries, predictive search for others
//     const useRegularSearch = this.#mightBeSkuOrBarcode(searchTerm);

//     if (useRegularSearch) {
//       // Use regular search which supports SKU/barcode
//       const searchUrl = new URL(Theme.routes.search_url, location.origin);
//       searchUrl.searchParams.set('q', searchTerm);
//       searchUrl.searchParams.set('type', 'product');
//       searchUrl.searchParams.set('options[unavailable_products]', 'last');
//       searchUrl.searchParams.set('options[prefix]', 'last');

//       try {
//         const response = await fetch(searchUrl.toString(), {
//           headers: {
//             'X-Requested-With': 'XMLHttpRequest',
//             'Accept': 'text/html'
//           },
//           signal: abortController.signal
//         });

//         if (!response.ok || abortController.signal.aborted) return;

//         const html = await response.text();
//         const resultsMarkup = this.#convertSearchResultsToPredictiveFormat(html, searchTerm);

//         if (abortController.signal.aborted) return;

//         morph(predictiveSearchResults, resultsMarkup);

//         this.#setAriaExpanded(true);

//         // Use requestAnimationFrame to ensure DOM is updated before setting display
//         requestAnimationFrame(() => {
//           if (abortController.signal.aborted) return;

//           // Re-fetch contentWrapper after morph to ensure we have the correct element
//           const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
//           if (contentWrapper instanceof HTMLElement) {
//             // Remove display: none (including !important) and set to block to show the dropdown
//             // Use block for desktop (as per CSS), flex for mobile
//             const displayValue = this.#isDesktop ? 'block' : 'flex';
//             contentWrapper.style.setProperty('display', displayValue, 'important');
//           }
//         });

//         this.#resetScrollPositions();
//       } catch (error) {
//         if (abortController.signal.aborted) return;
//         throw error;
//       }
//     } else {
//       // Use predictive search for regular queries
//       const url = new URL(Theme.routes.predictive_search_url, location.origin);
//       url.searchParams.set('q', searchTerm);
//       url.searchParams.set('resources[limit_scope]', 'each');

//       sectionRenderer
//         .getSectionHTML(this.dataset.sectionId, false, url)
//         .then((resultsMarkup) => {
//           if (!resultsMarkup) return;

//           if (abortController.signal.aborted) return;

//           morph(predictiveSearchResults, resultsMarkup);

//           this.#setAriaExpanded(true);

//           // Use requestAnimationFrame to ensure DOM is updated before setting display
//           requestAnimationFrame(() => {
//             if (abortController.signal.aborted) return;

//             // Re-fetch contentWrapper after morph to ensure we have the correct element
//             const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
//             if (contentWrapper instanceof HTMLElement) {
//               // Remove display: none (including !important) and set to block to show the dropdown
//               // Use block for desktop (as per CSS), flex for mobile
//               const displayValue = this.#isDesktop ? 'block' : 'flex';
//               contentWrapper.style.setProperty('display', displayValue, 'important');
//             }
//           });

//           this.#resetScrollPositions();
//         })
//         .catch((error) => {
//           if (abortController.signal.aborted) return;
//           throw error;
//         });
//     }
//   }

//   /**
//    * Converts regular search results to predictive search format
//    * @param {string} searchResultsMarkup - HTML markup from regular search
//    * @param {string} searchTerm - The search term
//    * @returns {string} Predictive search formatted markup
//    */
//   #convertSearchResultsToPredictiveFormat(searchResultsMarkup, searchTerm) {
//     const parser = new DOMParser();
//     const doc = parser.parseFromString(searchResultsMarkup, 'text/html');

//     // Find products in search results - try multiple selectors
//     let productItems = Array.from(doc.querySelectorAll('.product-grid__item[data-product-id]'));
//     if (productItems.length === 0) {
//       productItems = Array.from(doc.querySelectorAll('li[data-product-id]'));
//     }
//     if (productItems.length === 0) {
//       productItems = Array.from(doc.querySelectorAll('[data-product-id]'));
//     }

//     if (productItems.length === 0) {
//       // Return no results message matching predictive search format
//       return `
//         <div id="predictive-search-results" class="predictive-search-dropdown" role="listbox" aria-label="Search results" aria-expanded="true" style="--color-shadow: rgb(var(--color-foreground-rgb) / var(--opacity-10-25));">
//           <div class="predictive-search-results__inner" data-search-results>
//             <p class="predictive-search-results__no-results">
//               No results found for "${searchTerm}"
//             </p>
//           </div>
//         </div>
//       `;
//     }

//     // Clone and adapt product items for predictive search format
//     // Limit to 8 products like predictive search does
//     const limitedItems = productItems.slice(0, 8);
//     let productsHTML = '';

//     limitedItems.forEach((item, index) => {
//       const clonedItem = item.cloneNode(true);
//       if (clonedItem instanceof HTMLElement) {
//         // Update classes and attributes for predictive search format
//         clonedItem.className = 'predictive-search-results__card predictive-search-results__card--product';
//         clonedItem.setAttribute('ref', 'resultsItems[]');
//         clonedItem.setAttribute('data-search-result-index', `search-results-${index + 1}`);
//         clonedItem.removeAttribute('data-page');

//         // Remove id to avoid conflicts
//         const oldId = clonedItem.getAttribute('id');
//         if (oldId) {
//           clonedItem.removeAttribute('id');
//         }

//         productsHTML += clonedItem.outerHTML;
//       }
//     });

//     // Build the full structure matching predictive search format
//     return `
//       <div id="predictive-search-results" class="predictive-search-dropdown" role="listbox" aria-label="Search results" aria-expanded="true" style="--color-shadow: rgb(var(--color-foreground-rgb) / var(--opacity-10-25));">
//         <div class="predictive-search-results__inner" data-search-results>
//           <div id="predictive-search-products" class="predictive-search-results__products">
//             <h4 class="predictive-search-results__title">Products</h4>
//             <ul class="predictive-search-results__list predictive-search-results__wrapper predictive-search-results__wrapper-products list-unstyled" role="listbox" aria-label="Products">
//               ${productsHTML}
//             </ul>
//           </div>
//         </div>
//       </div>
//     `;
//   }

//   /**
//    * Fetch the markup for the recently viewed products.
//    * @returns {Promise<string | null>} The markup for the recently viewed products.
//    */
//   async #getRecentlyViewedProductsMarkup() {
//     if (!this.dataset.sectionId) return null;

//     const viewedProducts = RecentlyViewed.getProducts();
//     if (viewedProducts.length === 0) return null;

//     const url = new URL(Theme.routes.search_url, location.origin);
//     url.searchParams.set('q', viewedProducts.map(/** @param {string} id */(id) => `id:${id}`).join(' OR '));
//     url.searchParams.set('resources[type]', 'product');

//     return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
//   }

//   #hideResetButton() {
//     const { resetButton } = this.refs;

//     resetButton.hidden = true;
//   }

//   #showResetButton() {
//     const { resetButton } = this.refs;

//     resetButton.hidden = false;
//   }

//   #createAbortController() {
//     const abortController = new AbortController();
//     if (this.#activeFetch) {
//       this.#activeFetch.abort();
//     }
//     this.#activeFetch = abortController;
//     return abortController;
//   }

//   #resetSearch = async () => {
//     const { predictiveSearchResults, searchInput } = this.refs;
//     const emptySectionId = 'predictive-search-empty';

//     this.#currentIndex = -1;
//     searchInput.value = '';
//     this.#hideResetButton();

//     const abortController = this.#createAbortController();
//     const url = new URL(window.location.href);
//     url.searchParams.delete('page');

//     const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
//     const parsedEmptySectionMarkup = new DOMParser()
//       .parseFromString(emptySectionMarkup, 'text/html')
//       .querySelector('.predictive-search-empty-section');

//     if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

//     /** This needs to be awaited and not .then so the DOM is already morphed
//      * when #closeResults is called and therefore the height is animated */
//     const viewedProducts = RecentlyViewed.getProducts();

//     if (viewedProducts.length > 0) {
//       const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
//       if (!recentlyViewedMarkup) return;

//       const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
//       const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
//       if (!recentlyViewedProductsHtml) return;

//       for (const child of recentlyViewedProductsHtml.children) {
//         if (child instanceof HTMLElement) {
//           child.setAttribute('ref', 'recentlyViewedWrapper');
//         }
//       }

//       const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
//       if (!collectionElement) return;
//       collectionElement.prepend(...recentlyViewedProductsHtml.children);
//     }

//     if (abortController.signal.aborted) return;

//     morph(predictiveSearchResults, parsedEmptySectionMarkup);

//     // Set aria-expanded to false when resetting search
//     this.#setAriaExpanded(false);

//     // Close the dropdown - re-fetch contentWrapper after morph
//     requestAnimationFrame(() => {
//       const contentWrapper = this.querySelector('.predictive-search-form__content-wrapper');
//       if (contentWrapper instanceof HTMLElement) {
//         contentWrapper.style.setProperty('display', 'none', 'important');
//       }
//     });

//     this.#resetScrollPositions();
//   };
// }

// if (!customElements.get('predictive-search-component')) {
//   customElements.define('predictive-search-component', PredictiveSearchComponent);
// }
